import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'src/shared/components/ui/select'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { PAGE_SIZES } from '../consts'

interface PaginationControlsProps {
  currentPage: number
  pageSize: number
  totalCount: number
  totalPages: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}

export const PaginationControls = ({
  currentPage,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: PaginationControlsProps) => {
  const { t } = useLocalization()

  return (
    <div className="flex items-center justify-between px-2 py-4 bg-white border-t rounded-b-lg">
      <div className="flex items-center space-x-2">
        <p className="text-sm text-muted-foreground">
          {t('Showing', 'Visar')}{' '}
          {totalCount > 0 ? (currentPage - 1) * pageSize + 1 : 0}{' '}
          {t('to', 'till')} {Math.min(currentPage * pageSize, totalCount)}{' '}
          {t('of', 'av')} {totalCount} {t('results', 'resultat')}
        </p>
      </div>

      <div className="flex items-center space-x-2">
        <p className="text-sm text-muted-foreground">
          {t('Rows per page', 'Rader per sida')}
        </p>
        <Select
          value={`${pageSize}`}
          onValueChange={(value) => {
            const newPageSize = Number(value)
            onPageSizeChange(newPageSize)
          }}
        >
          <SelectTrigger className="h-8 w-[70px]">
            <SelectValue placeholder={pageSize} />
          </SelectTrigger>
          <SelectContent side="top">
            {PAGE_SIZES.map((size) => (
              <SelectItem key={size} value={`${size}`}>
                {size}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-center space-x-2">
        <Button
          className="hidden h-8 w-8 p-0 lg:flex"
          onClick={() => onPageChange(1)}
          disabled={currentPage === 1}
        >
          <span className="sr-only">
            {t('Go to first page', 'Gå till första sidan')}
          </span>
          <ChevronLeft className="h-4 w-4" />
          <ChevronLeft className="h-4 w-4 -ml-1" />
        </Button>
        <Button
          className="h-8 w-8 p-0"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          <span className="sr-only">
            {t('Go to previous page', 'Gå till föregående sida')}
          </span>
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          className="h-8 w-8 p-0"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={!totalPages || currentPage >= totalPages}
        >
          <span className="sr-only">
            {t('Go to next page', 'Gå till nästa sida')}
          </span>
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          className="hidden h-8 w-8 p-0 lg:flex"
          onClick={() => onPageChange(totalPages || 1)}
          disabled={!totalPages || currentPage >= (totalPages || 1)}
        >
          <span className="sr-only">
            {t('Go to last page', 'Gå till sista sidan')}
          </span>
          <ChevronRight className="h-4 w-4" />
          <ChevronRight className="h-4 w-4 -ml-1" />
        </Button>
      </div>
    </div>
  )
}

